/**
 * Operations → Closed.
 *
 * The graveyard — every case that has reached Case Closed, the funnel's
 * terminal node. This is the terminal sink for the Settled exit: a matter
 * leaves Settled book the moment its placement flips Settled → Case Closed,
 * and Closed is where it lands, so nothing vanishes off the end of the
 * pipeline. No clock and no urgency ordering — closed cases carry no live work.
 *
 * LAYOUT (portal-imprint, D1=hybrid): a portal-style <Table>. This reverses the
 * 2026-05-22 card decision — but that decision's objections are resolved by the
 * merge: (1) "click-into-detail" — table rows open the shared CaseDetailPanel;
 * (2) "lone special case" — under D1 every terminal book is a table, so the
 * table IS the consistent terminal treatment; (3) a table RESTORES the
 * scannable settlement column the card had to give up. Anomalous rows (a closed
 * placement whose lead didn't resolve) are still shown, flagged — a graveyard
 * that hid them would defeat its purpose.
 *
 * `ClosedCard` (below) is unchanged — The Surface renders closed matters as
 * cards in its mixed-phase overlay.
 *
 * Read-only. Cases enter (close) upstream; the surface re-derives on each poll.
 *
 * Spec: engineering/command-center/docs/port-notes/closed.md (evicted 2026-08-29, #775 round 10)
 */

import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import type { ClosedCase } from "@command-center/shared";
import { BookHeading } from "@/components/book-heading";
import { SearchBar, matchesQuery } from "@/components/search-bar";
import { SurfaceTrigger } from "@/components/surface-trigger";
import { CaseDetailPanel } from "@/components/case-detail-panel";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { closedToDetail, type CaseDetail } from "@/lib/case-detail";
import { fmtCurrency, fmtDate } from "@/lib/format";
import { useClosed } from "@/lib/use-closed";

export function ClosedPage() {
  const closed = useClosed();
  const [query, setQuery] = useState("");
  // Lifted so a row stays open while you read it (portal parity: row → detail).
  const [detail, setDetail] = useState<CaseDetail | null>(null);

  if (closed.isLoading) {
    return <div className="p-8 text-sm text-plane-muted">Loading…</div>;
  }

  if (closed.isError) {
    return (
      <div className="p-8 text-sm text-error" data-testid="closed-error">
        Couldn't load Closed. It will retry automatically.
      </div>
    );
  }

  const cases = closed.data?.cases ?? [];
  const searching = query.trim() !== "";
  const visible = cases.filter((c) => matchesQuery(c.client, query));

  return (
    <section className="mx-auto max-w-4xl p-6" data-testid="closed">
      <BookHeading phaseKey="closed">
        <span className="text-sm text-plane-muted" data-testid="closed-count">
          {cases.length === 0
            ? ""
            : `${cases.length} closed case${cases.length === 1 ? "" : "s"}`}
        </span>
      </BookHeading>

      <SearchBar value={query} onChange={setQuery} testId="closed-search" />

      {visible.length === 0 ? (
        <div
          className="flex min-h-[50vh] items-center justify-center text-center"
          data-testid="closed-empty"
        >
          <p className="text-lg text-plane-muted">
            {searching ? "No matches." : "No closed cases yet."}
          </p>
        </div>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="cursor-default hover:bg-transparent">
                <TableHead>Client</TableHead>
                <TableHead>Settlement</TableHead>
                <TableHead>Referred by</TableHead>
                <TableHead>Handling firm</TableHead>
                <TableHead>Closed</TableHead>
                <TableHead>
                  <span className="sr-only">Watch</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((c) => (
                <ClosedRow
                  key={c.id}
                  closedCase={c}
                  onOpenDetail={() => setDetail(closedToDetail(c))}
                />
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {detail ? (
        <CaseDetailPanel
          key={
            detail.actionTarget?.placementId ??
            detail.leadActionTarget?.leadId ??
            detail.name
          }
          detail={detail}
          onClose={() => setDetail(null)}
          testId="closed-summary-panel"
        />
      ) : null}
    </section>
  );
}

/** One closed matter as a portal-style table row. The settlement figure is back
 *  in a scannable column. An unresolved case (status didn't flip) shows the
 *  anomaly marker in the client cell and keeps whatever the placement supplied;
 *  a lead-less row (broken join) has no watch trigger to show. */
function ClosedRow({
  closedCase: c,
  onOpenDetail,
}: {
  closedCase: ClosedCase;
  onOpenDetail: () => void;
}) {
  return (
    <TableRow onClick={onOpenDetail} data-testid="closed-card">
      <TableCell className="font-medium">
        {c.statusAnomaly ? (
          <span
            className="inline-flex items-center gap-1.5 text-muted-foreground"
            data-testid="closed-anomaly"
            title={`Closed placement not resolvable at Lead Status "Case Closed" — status didn't flip or the link is broken. Lead record: ${c.leadRecordId ?? "none"}`}
          >
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-warning" />
            <span className="italic">{c.client ?? "Unresolved case"}</span>
          </span>
        ) : (
          c.client
        )}
      </TableCell>
      <TableCell className="tabular-nums" data-testid="closed-amount">
        {c.settlementAmount !== null ? fmtCurrency(c.settlementAmount) : "—"}
      </TableCell>
      <TableCell
        className={c.originatingFirmName ? undefined : "text-muted-foreground"}
        data-testid="closed-of-firm"
      >
        {c.originatingFirmName ?? "—"}
      </TableCell>
      <TableCell
        className={c.handlingFirmName ? undefined : "text-muted-foreground"}
        data-testid="closed-hf-firm"
      >
        {c.handlingFirmName ?? "—"}
      </TableCell>
      <TableCell
        className="tabular-nums text-muted-foreground"
        data-testid="closed-date"
      >
        {c.caseClosedDate !== null ? fmtDate(c.caseClosedDate, { year: true }) : "—"}
      </TableCell>
      <TableCell className="w-0 text-right" onClick={(e) => e.stopPropagation()}>
        {c.leadRecordId !== null ? <SurfaceTrigger leadId={c.leadRecordId} /> : null}
      </TableCell>
    </TableRow>
  );
}

/** One closed matter as a card — used by The Surface's mixed-phase overlay.
 *  client + settlement amount, the two firms, the close date. An unresolved
 *  case renders with a warning marker in place of the client name. */
export function ClosedCard({
  closedCase: c,
  onOpenDetail,
}: {
  closedCase: ClosedCase;
  /** When provided (on The Surface), a "Case detail →" link opens the panel. */
  onOpenDetail?: () => void;
}) {
  return (
    <li className="rounded-lg border bg-card text-card-foreground px-4 py-3" data-testid="closed-card">
      {/* Top line: who + settlement on the left, close date on the right. */}
      <div className="flex items-start gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          {c.statusAnomaly ? (
            <span
              className="inline-flex items-center gap-1.5 text-muted-foreground"
              data-testid="closed-anomaly"
              title={`Closed placement not resolvable at Lead Status "Case Closed" — status didn't flip or the link is broken. Lead record: ${c.leadRecordId ?? "none"}`}
            >
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-warning" />
              <span className="italic">{c.client ?? "Unresolved case"}</span>
            </span>
          ) : (
            <span className="text-sm font-medium">{c.client}</span>
          )}
          <span
            className="text-sm font-medium tabular-nums"
            data-testid="closed-amount"
          >
            {c.settlementAmount !== null ? fmtCurrency(c.settlementAmount) : "—"}
          </span>
        </div>
        <span className="shrink-0 text-right">
          <span
            className="text-sm font-semibold tabular-nums text-muted-foreground"
            data-testid="closed-date"
          >
            {c.caseClosedDate !== null ? fmtDate(c.caseClosedDate, { year: true }) : "—"}
          </span>
          <span className="block text-[11px] text-muted-foreground">closed</span>
        </span>
        {/* A lead-less closed card (broken join) has nothing to float. */}
        {c.leadRecordId !== null ? (
          <SurfaceTrigger leadId={c.leadRecordId} />
        ) : null}
      </div>

      {/* The parties: originating + handling firm. No fee split or disbursement
          spine — that was Settled book's job; here the case is done. */}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <Party role="OF" name={c.originatingFirmName} testid="closed-of-firm" />
        <Party role="HF" name={c.handlingFirmName} testid="closed-hf-firm" />
      </div>

      {onOpenDetail ? (
        <button
          type="button"
          onClick={onOpenDetail}
          className="mt-2 text-xs font-medium text-interactive hover:underline"
          data-testid="closed-open-detail"
        >
          Case detail →
        </button>
      ) : null}
    </li>
  );
}

/**
 * One party — a role chip (OF / HF) and the firm name. Unlike the live books,
 * Closed shows a dash for a missing firm rather than just the chip: the
 * graveyard records what it has, and a blank firm on a closed case is itself
 * worth seeing.
 */
function Party({
  role,
  name,
  testid,
}: {
  role: "OF" | "HF";
  name: string | null;
  testid: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5" data-testid={testid}>
      <span className="rounded bg-muted px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {role}
      </span>
      <span className={name ? "font-medium" : "text-muted-foreground"}>
        {name ?? "—"}
      </span>
    </span>
  );
}

